'use client';

import React, { useEffect, useRef } from 'react';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';
import Image from 'next/image';
import { resolveImageAltText } from '@/lib/utils';

export default function AutoSlider({ block, position }) {
  const {
    title,
    description,
    background_color,
    text_color,
    slides,
    stats,
    _metadata
  } = block.auto_slider;

  const sliderRef = useRef(null);

  useEffect(() => {
    if (!sliderRef.current) return;

    const slider = new BlazeSlider(sliderRef.current, {
      all: {
        slideGap: '20px',
        slidesToShow: 1,
        loop: true,
        enableAutoplay: true,
        transitionTimingFunction: 'linear',
        stopAutoplayOnInteraction: false,
        draggable: false
      },
      '(min-width: 640px)': {
        autoplayInterval: 40,
        transitionDuration: 4000
      },
      '(max-width: 639px)': {
        autoplayInterval: 5000,
        transitionDuration: 600
      }
    });

    return () => slider.destroy();
  }, []);

  return (
    <>
      <section
        id={`${_metadata.uid}_${position}`}
        data-name={`auto-slider-${position}`}
        className={`auto-slider large-padding w-full text-[${text_color}] bg-[${background_color}]`}
      >
        <div>
          <div className="small-container mx-auto">
            <h2 className="h3 lg:h2 mb-4 text-center">{title}</h2>
            <div
              className="body-r lg:sh8 text-center"
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
          </div>
          {slides.length > 0 && (
            <div className={`blaze-slider mt-15`} ref={sliderRef}>
              <div className="blaze-container">
                <div className="blaze-track-container">
                  <ul className="blaze-track sm:max-w-65" tabIndex={0}>
                    {slides.map((slide, index) => {
                      const alt = resolveImageAltText(slide.image);
                      return (
                        <li
                          key={index}
                          className="my-auto max-h-81.25 w-full overflow-hidden sm:max-w-65"
                        >
                          <Image
                            src={slide.image.url}
                            width={slide.image.dimension.width}
                            height={slide.image.dimension.height}
                            sizes="(min-width: 640px) 260px, 100vw"
                            alt={alt}
                            className="h-auto w-full sm:w-auto sm:max-w-65"
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <button
                  id={`auto-slider-${position}-blaze-next`}
                  className="blaze-next"
                >
                  <span className="sr-only">next</span>
                </button>
              </div>
              <div className="blaze-pagination indicators-dark mt-10 hidden sm:mt-15"></div>
            </div>
          )}
          {stats.length > 0 && (
            <div className="small-container mx-auto grid grid-cols-2 gap-x-5 gap-y-10 pt-15 sm:gap-x-10 lg:grid-cols-4 lg:gap-x-20">
              {stats.map((stat, index) => {
                return (
                  <div key={index} className={`text-center`}>
                    <div className="stat-title mb-2">{stat.title}</div>
                    <div className="body-r">{stat.description}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
